import * as S from './styles';
import { useClient, useTables } from '@/hooks';
import { SYNC_INDISPENSABLE_TABLE_KEYS, useSyncOnDemand } from '@/hooks/useSyncOnDemand';
import { getOnStorage } from '@/shared';
import { copy } from '@/shared/commonFunctions';
import { ClientProps, TabCodes, TabParams } from '@/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListCompany, Modal, TabSlider, DependenciesClient } from '@/components';
import { addDays, isBefore } from 'date-fns';
import clienteMock from '@/mocks/clienteMock.json';
import { groupClientsByLoja } from '@/utils/clientDataTransform';

interface Props {
  children: any;
  forceReload?: boolean;
}

const ModalListCompanies = ({ children, forceReload = false }: Props) => {
  const { tables } = useTables();
  const { allClients, campanhaClientes } = useClient();
  const { isSyncing, syncIfNeeded } = useSyncOnDemand();

  const isLoading = useRef(false);
  const hasSyncedForModal = useRef(false);
  const eventos = tables.current.eventos;
  const [filter, setFilter] = useState('');
  const [isOpenedModal, openModal] = useState(false);
  const [localClient, setLocalClient] = useState(() => {
    return getOnStorage('selected_client') as ClientProps;
  });
  const [selectedTab, setSelectedTab] = useState('all' as TabCodes);

  const clientsWithMock = useMemo(() => {
    const groupedMockClients = groupClientsByLoja(clienteMock as any[]);
    console.log('groupedMockClients', groupedMockClients);
    const clients = allClients || [];
    const selectedClientStorage = getOnStorage('selected_client') as ClientProps | undefined;

    if (!groupedMockClients?.length) return clients;

    const clientsByCode = new Map(clients.map((client) => [client.COD_CLIENTE, client]));

    groupedMockClients.forEach((mockClient) => {
      const existingClient = clientsByCode.get(mockClient.COD_CLIENTE);
      if (!existingClient) {
        clientsByCode.set(mockClient.COD_CLIENTE, mockClient);
        return;
      }

      const existingLojas = existingClient?.lojas || [];
      const mockLojas = mockClient?.lojas || [];
      const mergedLojas = [...existingLojas];

      mockLojas.forEach((mockLoja) => {
        const hasLoja = mergedLojas.some((loja) => Number(loja.COD_LOJA) === Number(mockLoja.COD_LOJA));
        if (!hasLoja) mergedLojas.push(mockLoja);
      });

      const updatedClient = {
        ...existingClient,
        lojas: mergedLojas,
      };

      if (mergedLojas.length > 0) {
        const preferredLojaCode =
          Number(
            selectedClientStorage?.COD_CLIENTE === updatedClient.COD_CLIENTE
              ? selectedClientStorage?.COD_LOJA
              : existingClient?.COD_LOJA
          ) || Number(existingClient?.COD_LOJA) || Number(mockClient?.COD_LOJA);
        const activeLoja =
          mergedLojas.find((loja) => Number(loja.COD_LOJA) === preferredLojaCode) || mergedLojas[0];
        updatedClient.COD_LOJA = activeLoja.COD_LOJA;
        updatedClient.COD_SEGMENTO = activeLoja.COD_SEGMENTO as ClientProps['COD_SEGMENTO'];
        updatedClient.COD_RCA = activeLoja.COD_RCA;
        updatedClient.COD_TABELA_VENDA = activeLoja.COD_TABELA_VENDA;
        updatedClient.COD_REGIME_TRIB = activeLoja.COD_REGIME_TRIB;
        updatedClient.COD_CONDICAO_MAX = activeLoja.COD_CONDICAO_MAX;
        updatedClient.COD_CONDICAO_PADRAO = activeLoja.COD_CONDICAO_PADRAO;
        updatedClient.COD_PAGAMENTO = activeLoja.COD_PAGAMENTO as ClientProps['COD_PAGAMENTO'];
        updatedClient.COD_ROTA = activeLoja.COD_ROTA;
      }

      clientsByCode.set(mockClient.COD_CLIENTE, updatedClient);
    });

    return Array.from(clientsByCode.values());
  }, [allClients]);

  const loadcurrentClient = useCallback(() => {
    const currClient = getOnStorage('selected_client');

    if (currClient) setLocalClient(currClient);
    isLoading.current = false;
  }, []);

  const setClient = useCallback(
    (client: ClientProps | undefined) => {
      if (!client) return;
      setLocalClient(client);
    },
    [setLocalClient]
  );

  /**
   * GARANTE SINCRONIZAÇÃO QUANDO MODAL ABRE SEM DADOS DE CLIENTES
   * Reutiliza infraestrutura existente via useSyncOnDemand
   * Caso não haja dados de clientes, força sincronização
   */
  const handleModalOpen = useCallback(async () => {
    openModal(true);

    // Se não há dados de clientes, força sincronização
    if (!allClients?.length && !hasSyncedForModal.current && !isSyncing) {
      console.log('ModalListCompanies: Sem dados de clientes - forçando sincronização...');
      hasSyncedForModal.current = true;

      const syncSuccess = await syncIfNeeded({
        requiredTables: [...SYNC_INDISPENSABLE_TABLE_KEYS],
        force: false, // Só sincroniza se realmente não há dados
      });

      if (!syncSuccess) {
        console.error('ModalListCompanies: Falha na sincronização de dados de clientes');
      } else {
        console.log('ModalListCompanies: Dados de clientes sincronizados com sucesso');
      }
    }
  }, [allClients?.length, isSyncing, syncIfNeeded]);

  useEffect(() => {
    if (allClients?.length || isLoading.current) return;

    isLoading.current = true;
    loadcurrentClient();
  }, [loadcurrentClient, allClients, isLoading]);

  // Reset do flag quando dados chegam
  useEffect(() => {
    if (allClients?.length > 0) {
      hasSyncedForModal.current = false;
    }
  }, [allClients?.length]);

  const listTabs: TabParams[] = useMemo(
    () => [
      {
        disabled: false,
        code: 'all',
        title: 'Todos',
        notification: clientsWithMock?.length,
      },
      {
        disabled: false,
        code: 'tickets',
        title: 'Boletos',
        notification: clientsWithMock?.filter((c) => eventos?.[c.COD_CLIENTE])?.length || 0,
      },
      {
        disabled: false,
        code: 'activeTickets',
        title: 'Boletos Vencidos',
        notification:
          clientsWithMock?.filter((c) =>
            eventos?.[c.COD_CLIENTE]?.boletos?.some((b) => b.parcelas?.some((p) => p.status === 'VENCIDO'))
          )?.length || 0,
      },
      {
        disabled: false,
        code: 'inactiveTickets',
        title: 'Boletos a Vencer',
        notification:
          clientsWithMock?.filter((c) =>
            eventos?.[c.COD_CLIENTE]?.boletos?.some((b) =>
              b.parcelas?.some((p) => p.status === 'ABERTO' && isBefore(p.dt_vencimento, addDays(new Date(), 5)))
            )
          )?.length || 0,
      },
      {
        disabled: false,
        code: 'active',
        title: 'Ativos',
        notification: clientsWithMock?.filter((c) => c.ST_COMPRA != 'I').length || 0,
      },
      {
        disabled: false,
        code: 'blocked',
        title: 'Bloqueados',
        notification: clientsWithMock?.filter((c) => c.ST_COMPRA === 'I').length || 0,
      },
      {
        disabled: !clientsWithMock?.filter((c) => c.daysWithoutPurchase).length,
        code: 'noPurchases',
        title: 'Sem comprar',
        notification: clientsWithMock?.filter((c) => c.daysWithoutPurchase).length || 0,
      },
      {
        disabled: false,
        code: 'approved',
        title: 'Positivou na Campanha',
        notification: clientsWithMock?.filter((c) => {
          const ativos = campanhaClientes?.ativo?.clientes || [];
          return ativos?.includes(c.COD_CLIENTE);
        }).length,
      },
      {
        disabled: false,
        code: 'notApproved',
        title: 'Não positivou na campanha',
        notification: clientsWithMock?.filter((c) => {
          const inativos = campanhaClientes?.inativo?.cliente || [];
          return inativos?.includes(c.COD_CLIENTE);
        }).length,
      },
      // {
      //   disabled: true,
      //   code: "titles",
      //   title: "Títulos",
      //   notification: clients.filter((c) => c.notifications).length,
      // }
    ],
    [campanhaClientes, clientsWithMock]
  );

  const listClients = useMemo((): ClientProps[] => {
    let filteredList: ClientProps[] = [];
    if (filter) {
      filteredList =
        clientsWithMock?.filter((c) => {
          const toFilter = copy(c);

          delete toFilter.progress1;
          delete toFilter.progress2;
          delete toFilter.progress3;
          delete toFilter.progress4;
          delete toFilter.COD_PAGAMENTO;

          return Object.values(toFilter).toString().toLowerCase().includes(filter.toLocaleLowerCase());
        }) || [];
    } else if (selectedTab === 'all') {
      filteredList = clientsWithMock || [];
    } else if (selectedTab === 'tickets') {
      filteredList = clientsWithMock?.filter((c) => eventos?.[c.COD_CLIENTE]) || [];
    } else if (selectedTab === 'activeTickets') {
      filteredList =
        clientsWithMock?.filter((c) =>
          eventos?.[c.COD_CLIENTE]?.boletos?.some((b) => b.parcelas?.some((p) => p.status === 'VENCIDO'))
        ) || [];
    } else if (selectedTab === 'inactiveTickets') {
      filteredList =
        clientsWithMock?.filter((c) =>
          eventos?.[c.COD_CLIENTE]?.boletos?.some((b) =>
            b.parcelas?.some((p) => p.status === 'ABERTO' && isBefore(p.dt_vencimento, addDays(new Date(), 5)))
          )
        ) || [];
    } else if (selectedTab === 'noPurchases') {
      filteredList = clientsWithMock?.filter((c) => c.daysWithoutPurchase >= 30) || [];
    } else if (selectedTab === 'suspended') {
      filteredList = clientsWithMock?.filter((c) => c.suspended) || [];
    } else if (selectedTab === 'blocked') {
      filteredList = clientsWithMock?.filter((c) => c.ST_COMPRA === 'I') || [];
    } else if (selectedTab === 'active') {
      filteredList = clientsWithMock?.filter((c) => c.ST_COMPRA !== 'I') || [];
    } else if (selectedTab === 'notApproved') {
      filteredList =
        clientsWithMock?.filter((c) => {
          const inativos = campanhaClientes?.inativo?.cliente || [];
          return inativos?.includes(c.COD_CLIENTE);
        }) || [];
    } else if (selectedTab === 'approved') {
      filteredList =
        clientsWithMock?.filter((c) => {
          const ativos = campanhaClientes?.ativo?.clientes || [];
          return ativos?.includes(c.COD_CLIENTE);
        }) || [];
    } else {
      filteredList = clientsWithMock?.filter((c) => c.notifications) || [];
    }
    return filteredList;
  }, [filter, clientsWithMock, campanhaClientes, selectedTab]);

  const isEnabled = useMemo(() => {
    return !!localClient?.COD_CLIENTE;
  }, [localClient?.COD_CLIENTE]);

  const { selectedClient } = useClient();

  useEffect(() => {
    if (listClients?.length === 0 || !selectedClient?.COD_CLIENTE || !isOpenedModal) return;

    const listCompaniesContent = document.getElementById('list-companies-content') as HTMLDivElement;
    const scrollToSelectedClient = () => {
      const selectedClientElement = document.getElementById(`client-${selectedClient?.COD_CLIENTE}`) as HTMLElement;

      if (listCompaniesContent && selectedClientElement) {
        const containerRect = listCompaniesContent.getBoundingClientRect();
        const elementRect = selectedClientElement.getBoundingClientRect();
        const distanceToTop = elementRect.top - containerRect.top + listCompaniesContent.scrollTop - 20;

        listCompaniesContent.scrollTo({ top: distanceToTop, behavior: 'smooth' });
      }
    };

    setTimeout(scrollToSelectedClient, 1000);
  }, [isOpenedModal]);

  return (
    <>
      <S.ButtonSpan onClick={handleModalOpen} data-test="modal-companies-button-open">
        {children}
      </S.ButtonSpan>

      <Modal fullHeight id="list-companies" isOpen={isOpenedModal} closeModal={openModal} isScrollable={true}>
        <S.ModalBody>
          {/* Adicione este wrapper se ele não existir */}
          <S.ModalTitle>{isSyncing ? 'Sincronizando dados dos clientes...' : 'Para iniciar, selecione um cliente'}</S.ModalTitle>
          <S.InputContent>
            <S.SearchIcon $isgray size={24} />

            <S.InputModal
              value={filter}
              placeholder="Busque por cliente"
              data-test="modal-list-companies-input-search"
              onChange={(event: any) => setFilter(event.target.value.trim())}
            />
          </S.InputContent>
          <S.ContentTabSlider>
            <TabSlider lightMode listTabs={listTabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
          </S.ContentTabSlider>
          <S.ContentListCompany id="list-companies-content">
            <ListCompany
              clients={listClients}
              selectedClient={localClient}
              setLocalClient={setClient}
              isModalOpen={isOpenedModal}
            />
          </S.ContentListCompany>
          {isEnabled && isOpenedModal && (
            <DependenciesClient
              forceReload={forceReload}
              selectedClient={localClient}
              cbFunction={() => {
                openModal(false);
              }}
            />
          )}
        </S.ModalBody>
        {/* Feche o wrapper */}
      </Modal>
    </>
  );
};

export default ModalListCompanies;
